import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Award,
  ArrowLeft,
  RotateCw,
  Home,
  X,
  PlayCircle,
  AlertCircle,
  UserCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type {
  IFlat,
  IBuilding,
  IAllotment,
  IProvisionalAllotment,
  IProvisionalWaitlistItem,
} from "../types/quarters.types";

interface EligibleUser {
  _id: string;
  name: string;
  email: string;
  role: string;
}

const AllotmentEngine = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<"rank" | "auto" | "manual" | "active">("rank");

  // Supporting state
  const [vacantFlats, setVacantFlats] = useState<IFlat[]>([]);
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[]>([]);
  const [activeAllotments, setActiveAllotments] = useState<IAllotment[]>([]);

  // 1. Ranking Engine Form
  const [tieBreaker, setTieBreaker] = useState("oldest_application");

  // 2. Auto Allotment State
  const [selectedQuarterType, setSelectedQuarterType] = useState("");
  const [provisionalAllotments, setProvisionalAllotments] = useState<IProvisionalAllotment[]>([]);
  const [provisionalWaitlist, setProvisionalWaitlist] = useState<IProvisionalWaitlistItem[]>([]);
  const [hasAutoRun, setHasAutoRun] = useState(false);

  // 3. Manual Allotment Form
  const [manualForm, setManualForm] = useState({
    userId: "",
    flatId: "",
    remarks: "",
  });

  // 4. Allotment Actions Modal
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedAllotment, setSelectedAllotment] = useState<any>(null);
  const [actionForm, setActionForm] = useState({
    action: "vacate", // vacate, extend, cancel, transfer, evict
    remarks: "",
    date: "",
  });

  const loadData = useCallback(async () => {
    try {
      // Load active allotments
      const allotRes = await quartersService.getAllotments({ status: "active" });
      if (allotRes.success) setActiveAllotments(allotRes.data);

      // Load vacant flats
      const flatsRes = await quartersService.getFlats({ status: "vacant" });
      if (flatsRes.success) setVacantFlats(flatsRes.data);

      // Fetch users (candidates)
      const usersRes = await apiRequest("/auth/users");
      if (usersRes?.success) {
        setEligibleUsers(usersRes.data.filter((u: any) => u.role !== "super_admin"));
      }
    } catch (err) {
      console.error(err);
    }
  }, [apiRequest, quartersService]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Ranking Engine execution
  const handleRunRanking = async () => {
    setLoading(true);
    try {
      const res = await quartersService.runRankingEngine(tieBreaker);
      if (res.success) {
        toast.success(res.message);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to execute ranking engine");
    } finally {
      setLoading(false);
    }
  };

  // Auto Allotment Dry-Run execution
  const handleRunAutoAllotment = async () => {
    setLoading(true);
    try {
      const res = await quartersService.runAutoAllotment(selectedQuarterType || undefined);
      if (res.success) {
        setProvisionalAllotments(res.provisionalAllotments || []);
        setProvisionalWaitlist(res.waitlist || []);
        setHasAutoRun(true);
        toast.success(`Generated provisional preview with ${res.provisionalAllotments.length} matching allocations.`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to generate provisional allotments");
    } finally {
      setLoading(false);
    }
  };

  // Confirm Auto Allotments
  const handleConfirmAutoAllotments = async () => {
    if (!window.confirm(`Are you sure you want to commit these ${provisionalAllotments.length} allotments? This updates flat inventory and dispatches notifications.`)) return;
    setLoading(true);
    try {
      const res = await quartersService.confirmAutoAllotments(provisionalAllotments, provisionalWaitlist);
      if (res.success) {
        toast.success(res.message);
        setProvisionalAllotments([]);
        setProvisionalWaitlist([]);
        setHasAutoRun(false);
        setActiveSubTab("active");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to confirm allotments");
    } finally {
      setLoading(false);
    }
  };

  // Manual Allotment execution
  const handleManualAllot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualForm.userId || !manualForm.flatId) {
      toast.error("User and Flat selections are required");
      return;
    }
    setLoading(true);
    try {
      const res = await quartersService.runManualAllotment(manualForm);
      if (res.success) {
        toast.success("Manual allotment created successfully!");
        setManualForm({ userId: "", flatId: "", remarks: "" });
        setActiveSubTab("active");
      }
    } catch (err: any) {
      toast.error(err.message || "Manual allotment failed");
    } finally {
      setLoading(false);
    }
  };

  // Allotment Actions Execution (Extend, Vacate, Evict, etc.)
  const handleOpenActionModal = (allotment: any) => {
    setSelectedAllotment(allotment);
    setActionForm({
      action: "vacate",
      remarks: "",
      date: new Date().toISOString().split("T")[0],
    });
    setIsActionModalOpen(true);
  };

  const handleActionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAllotment) return;
    setLoading(true);
    try {
      const res = await quartersService.executeAllotmentAction(
        selectedAllotment._id,
        actionForm.action,
        actionForm.remarks,
        actionForm.date
      );
      if (res.success) {
        toast.success(res.message);
        setIsActionModalOpen(false);
        loadData();
      }
    } catch (err: any) {
      toast.error(err.message || "Allotment update action failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <Award className="w-8 h-8 text-blue-600" /> Accommodation Allotment Suite
          </h1>
        </div>
      </div>

      {/* Internal Navigation SubTabs */}
      <div className="flex border-b border-slate-200 mb-8 overflow-x-auto gap-2">
        {[
          { id: "rank", label: "Ranking Engine", icon: RotateCw },
          { id: "auto", label: "Automatic Allotment", icon: PlayCircle },
          { id: "manual", label: "Manual Override Allotment", icon: UserCheck },
          { id: "active", label: "Active Allotments & History", icon: Home },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id as any)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold border-b-2 uppercase tracking-wide transition whitespace-nowrap ${
                activeSubTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1: Ranking Engine */}
      {activeSubTab === "rank" && (
        <div className="bg-white border border-slate-200 p-8 rounded-3xl shadow-sm max-w-xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <RotateCw className={`w-12 h-12 text-blue-600 mx-auto ${loading ? "animate-spin" : ""}`} />
            <h2 className="text-lg font-bold text-slate-800 font-serif">Run Seniority Ranking Engine</h2>
            <p className="text-slate-400 text-xs leading-relaxed">
              This engine pulls all submitted applications, dynamically computes their seniority scores based on the current active weights configuration, and logs their roster placement order.
            </p>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">Tie-Breaker Strategy Rule</label>
              <select
                value={tieBreaker}
                onChange={(e) => setTieBreaker(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="oldest_application">Oldest Application (Submission Date ASC)</option>
                <option value="joining_date">Seniority (Service Joining Date ASC)</option>
                <option value="older_age">Age seniority (DOB Date ASC)</option>
                <option value="lottery">Random Stable Lottery</option>
              </select>
            </div>

            <button
              onClick={handleRunRanking}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
            >
              <RotateCw className="w-4 h-4" />
              {loading ? "Recalculating Roster..." : "Execute Ranking Engine"}
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: Automatic Allotment Engine */}
      {activeSubTab === "auto" && (
        <div className="space-y-8">
          <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4 items-end max-w-3xl mx-auto">
            <div className="space-y-1 md:col-span-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase">Target Flat Quarter Type (Optional)</label>
              <select
                value={selectedQuarterType}
                onChange={(e) => setSelectedQuarterType(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Accommodation Types</option>
                <option value="Type I">Type I</option>
                <option value="Type II">Type II</option>
                <option value="Type III">Type III</option>
                <option value="Type IV">Type IV</option>
                <option value="Type V">Type V</option>
              </select>
            </div>
            <button
              onClick={handleRunAutoAllotment}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold py-2.5 text-xs rounded-xl transition shadow-md shadow-blue-200 flex items-center justify-center gap-1.5"
            >
              <PlayCircle className="w-4.5 h-4.5" />
              Generate Provisional Preview
            </button>
          </div>

          {hasAutoRun && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left 2 cols: Provisional allotments */}
              <div className="lg:col-span-2 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-slate-800 font-bold text-base font-serif">Provisional Matches</h3>
                    <p className="text-slate-400 text-xs">Matching vacant flats to candidates based on category quotas</p>
                  </div>
                  {provisionalAllotments.length > 0 && (
                    <button
                      onClick={handleConfirmAutoAllotments}
                      disabled={loading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-md shadow-emerald-200"
                    >
                      Confirm Allotments
                    </button>
                  )}
                </div>

                {provisionalAllotments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 text-[10px] font-bold uppercase">
                          <th className="px-4 py-3 text-left">App Number</th>
                          <th className="px-4 py-3 text-left">Applicant</th>
                          <th className="px-4 py-3 text-left">Allotted Flat</th>
                          <th className="px-4 py-3 text-left">Matching Rule / Reason</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs text-slate-700 divide-y divide-slate-100">
                        {provisionalAllotments.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/50">
                            <td className="px-4 py-3 font-bold">{item.applicationNumber}</td>
                            <td className="px-4 py-3 font-semibold text-slate-800">{item.userName}</td>
                            <td className="px-4 py-3 font-bold text-blue-700">{item.flatNumber} ({item.quarterType})</td>
                            <td className="px-4 py-3 text-slate-500">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-10 bg-slate-50/50 rounded-2xl">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No provisional allotments possible.</p>
                    <p className="text-slate-400 text-xs mt-0.5">Either no matching vacant flats or no ranked applications.</p>
                  </div>
                )}
              </div>

              {/* Right col: Waitlist preview */}
              <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-4">
                <h3 className="text-slate-800 font-bold text-base font-serif border-b border-slate-100 pb-3">
                  Resulting Waitlist
                </h3>
                {provisionalWaitlist.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                    {provisionalWaitlist.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center border border-slate-100 p-3 rounded-xl bg-slate-50/50"
                      >
                        <div>
                          <p className="text-slate-800 font-bold text-xs">{item.userName}</p>
                          <p className="text-slate-400 text-[10px]">App: {item.applicationNumber} | Score: {item.score}</p>
                        </div>
                        <span className="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold">
                          WL-{item.waitlistPosition}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-slate-400 text-xs italic">Waitlist remains empty.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: Manual Allotment Override */}
      {activeSubTab === "manual" && (
        <div className="bg-white border border-slate-200 p-8 rounded-3xl shadow-sm max-w-xl mx-auto space-y-6">
          <div className="text-center space-y-1.5">
            <UserCheck className="w-12 h-12 text-blue-600 mx-auto" />
            <h2 className="text-lg font-bold text-slate-800 font-serif">Perform Manual Flat Allotment</h2>
            <p className="text-slate-400 text-xs">
              Assign any vacant flat directly to an employee profile, overriding the automated queue ranking.
            </p>
          </div>

          <form onSubmit={handleManualAllot} className="space-y-4 border-t border-slate-100 pt-6">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">Allottee Employee Profile*</label>
              <select
                required
                value={manualForm.userId}
                onChange={(e) => setManualForm({ ...manualForm, userId: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">Select Employee</option>
                {eligibleUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">Available Vacant Quarters*</label>
              <select
                required
                value={manualForm.flatId}
                onChange={(e) => setManualForm({ ...manualForm, flatId: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">Select Vacant Flat</option>
                {vacantFlats.map((f) => {
                  const building = typeof f.buildingId === "object" ? f.buildingId as IBuilding : null;
                  return (
                    <option key={f._id} value={f._id}>
                      {f.flatNumber} - {building?.name || "?"} ({building?.quarterType || "?"})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">Allotment Override Justification Remarks</label>
              <textarea
                value={manualForm.remarks}
                onChange={(e) => setManualForm({ ...manualForm, remarks: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                rows={3}
                placeholder="Reason for manual allocation override..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-blue-200 flex items-center justify-center gap-1.5"
            >
              <UserCheck className="w-4 h-4" />
              {loading ? "Allocating flat..." : "Confirm Manual Allotment"}
            </button>
          </form>
        </div>
      )}

      {/* TAB 4: Active Allotments & occupancy list */}
      {activeSubTab === "active" && (
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          {activeAllotments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100 text-slate-500 text-xs font-semibold uppercase">
                    <th className="px-6 py-4 text-left">Occupant</th>
                    <th className="px-6 py-4 text-left">Flat Number</th>
                    <th className="px-6 py-4 text-left">Building</th>
                    <th className="px-6 py-4 text-left">Dates</th>
                    <th className="px-6 py-4 text-left">Allotment Type</th>
                    <th className="px-6 py-4 text-center">Status</th>
                    <th className="px-6 py-4 text-center">Update Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {activeAllotments.map((allot) => {
                    const allotUser = typeof allot.userId === "object"
                      ? allot.userId as { name: string; email: string }
                      : null;
                    const allotFlat = typeof allot.flatId === "object" ? allot.flatId as IFlat : null;
                    const allotBuilding = allotFlat && typeof allotFlat.buildingId === "object"
                      ? allotFlat.buildingId as IBuilding
                      : null;
                    return (
                    <tr key={allot._id} className="hover:bg-slate-50/30 transition">
                      <td className="px-6 py-4">
                        <p className="font-bold text-slate-800">{allotUser?.name ?? "—"}</p>
                        <p className="text-slate-400 text-xs">{allotUser?.email ?? ""}</p>
                      </td>
                      <td className="px-6 py-4 font-bold text-blue-700">{allotFlat?.flatNumber ?? "—"}</td>
                      <td className="px-6 py-4 font-semibold text-slate-700">
                        {allotBuilding?.name ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        <p>Allotted: {new Date(allot.allottedAt).toLocaleDateString()}</p>
                        {allot.vacatingDate && (
                          <p>Target Vacate: {new Date(allot.vacatingDate).toLocaleDateString()}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 capitalize font-medium text-slate-600">{allot.allotmentType}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-bold uppercase">
                          {allot.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleOpenActionModal(allot)}
                          className="bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg transition"
                        >
                          Modify State
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-20">
              <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-700">No Active Allotments</h2>
              <p className="text-slate-500 text-sm mt-1">Run automatic allotment or manual allot to assign houses.</p>
            </div>
          )}
        </div>
      )}

      {/* Modify Occupancy Action Modal */}
      {isActionModalOpen && selectedAllotment && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
            <div className="flex justify-between items-center bg-slate-50 border-b border-slate-100 px-6 py-4">
              <h3 className="text-slate-800 font-bold text-base font-serif">
                Modify Allotment - Flat {selectedAllotment.flatId?.flatNumber}
              </h3>
              <button
                onClick={() => setIsActionModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleActionSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase">State Action*</label>
                <select
                  value={actionForm.action}
                  onChange={(e) => setActionForm({ ...actionForm, action: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                >
                  <option value="vacate">Vacate (Employee leaves peacefully)</option>
                  <option value="extend">Extend occupancy term</option>
                  <option value="cancel">Cancel allotment registration</option>
                  <option value="transfer">Transfer to other residence</option>
                  <option value="evict">Evict occupant (Forced eviction)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase">Action Date*</label>
                <input
                  type="date"
                  required
                  value={actionForm.date}
                  onChange={(e) => setActionForm({ ...actionForm, date: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase">Action Remarks / Reasons*</label>
                <textarea
                  required
                  value={actionForm.remarks}
                  onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
                  rows={3}
                  placeholder="Reasons for modifying allotment state..."
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setIsActionModalOpen(false)}
                  className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-blue-200"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllotmentEngine;
